import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query"

import type { TransportOperationName } from "@/types_api/backend/transport/operations"

import {
  getTransportInfiniteOperationQueryKey,
  getTransportOperationQueryKey,
  getTransportPaginatedOperationQueryKey,
  getTransportQueryOrganizationSlug,
  getTransportQueryWorkspaceId,
} from "./transportQueryKeys"

interface TransportQueryInvalidationEvent {
  exact: boolean
  operation: TransportOperationName
  queryKey: QueryKey
}

type TransportQueryInvalidationListener = (
  event: TransportQueryInvalidationEvent,
) => void

const invalidationListeners = new WeakMap<
  QueryClient,
  Set<TransportQueryInvalidationListener>
>()

export function subscribeToTransportQueryInvalidation(
  queryClient: QueryClient,
  listener: TransportQueryInvalidationListener,
): () => void {
  const listeners = invalidationListeners.get(queryClient) ?? new Set()
  listeners.add(listener)
  invalidationListeners.set(queryClient, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      invalidationListeners.delete(queryClient)
    }
  }
}

export function notifyTransportQueryInvalidation(
  queryClient: QueryClient,
  event: TransportQueryInvalidationEvent,
): void {
  invalidationListeners.get(queryClient)?.forEach((listener) => listener(event))
}

export function doesTransportQueryInvalidationMatch(
  event: TransportQueryInvalidationEvent,
  queryKey: QueryKey,
): boolean {
  if (event.exact) {
    return hashKey(event.queryKey) === hashKey(queryKey)
  }
  if (queryKey.length < event.queryKey.length) {
    return false
  }
  return (
    hashKey(event.queryKey) ===
    hashKey(queryKey.slice(0, event.queryKey.length))
  )
}

export function invalidateTransportOperationQueryKeys(
  queryClient: QueryClient,
  organizationSlug: string | null,
  operations: readonly TransportOperationName[],
  workspaceId: string | null = null,
  options?: { refetchType?: "active" | "none" },
): Promise<void> {
  const refetchType = options?.refetchType ?? "active"
  return Promise.all(
    operations.flatMap((operation) => {
      const queryOrganizationSlug = getTransportQueryOrganizationSlug(
        operation,
        organizationSlug,
      )
      const operationWorkspaceId = getTransportQueryWorkspaceId(
        operation,
        workspaceId,
      )
      const queryKey = getTransportOperationQueryKey(
        operation,
        queryOrganizationSlug,
        operationWorkspaceId,
      )
      notifyTransportQueryInvalidation(queryClient, {
        exact: false,
        operation,
        queryKey,
      })

      return [
        queryClient.invalidateQueries({
          queryKey,
          refetchType,
        }),
        queryClient.invalidateQueries({
          queryKey: getTransportPaginatedOperationQueryKey(
            operation,
            queryOrganizationSlug,
            operationWorkspaceId,
          ),
          refetchType,
        }),
        queryClient.invalidateQueries({
          queryKey: getTransportInfiniteOperationQueryKey(
            operation,
            queryOrganizationSlug,
            operationWorkspaceId,
          ),
          refetchType,
        }),
      ]
    }),
  ).then(() => undefined)
}
